export type JsonPatchOperation =
  | { op: "add"; path: string; value: unknown }
  | { op: "remove"; path: string }
  | { op: "replace"; path: string; value: unknown }
  | { op: "move"; from: string; path: string }
  | { op: "copy"; from: string; path: string }
  | { op: "test"; path: string; value: unknown }

export class JsonPatchError extends Error {
  constructor(
    message: string,
    public operation: JsonPatchOperation,
    public index: number
  ) {
    super(message)
    this.name = "JsonPatchError"
  }
}

/**
 * Parse a JSON Pointer (RFC 6901) string into path segments.
 * Handles ~0 (escaped ~) and ~1 (escaped /) per spec.
 */
export function parseJsonPointer(path: string): string[] {
  if (path === "") return []
  if (!path.startsWith("/")) {
    throw new Error(`Invalid JSON Pointer: must start with "/" or be empty: "${path}"`)
  }
  return path
    .substring(1)
    .split("/")
    .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"))
}

/**
 * Get a value at a JSON Pointer path.
 */
export function getByPath(obj: unknown, path: string): unknown {
  const segments = parseJsonPointer(path)
  let current: unknown = obj
  for (const seg of segments) {
    if (current === null || current === undefined) {
      throw new Error(`Cannot traverse into null/undefined at segment "${seg}"`)
    }
    if (Array.isArray(current)) {
      const index = parseArrayIndex(seg, current.length, false)
      current = current[index]
    } else if (typeof current === "object") {
      current = (current as Record<string, unknown>)[seg]
    } else {
      throw new Error(`Cannot traverse into primitive at segment "${seg}"`)
    }
  }
  return current
}

/**
 * Immutably set a value at a JSON Pointer path.
 */
export function setByPath<T>(obj: T, path: string, value: unknown): T {
  const segments = parseJsonPointer(path)
  if (segments.length === 0) return value as T
  return immutableSet(obj, { segments, depth: 0, value }) as T
}

/**
 * Immutably add a value at a JSON Pointer path.
 * For arrays, inserts at the specified index. "-" means append.
 */
export function addByPath<T>(obj: T, path: string, value: unknown): T {
  const segments = parseJsonPointer(path)
  if (segments.length === 0) return value as T
  return immutableAdd(obj, { segments, depth: 0, value }) as T
}

/**
 * Immutably remove a value at a JSON Pointer path.
 */
export function removeByPath<T>(obj: T, path: string): T {
  const segments = parseJsonPointer(path)
  if (segments.length === 0) {
    throw new Error("Cannot remove root document")
  }
  return immutableRemove(obj, segments, 0) as T
}

/**
 * Apply an array of JSON Patch operations atomically.
 * If any operation fails (including test), no changes are applied.
 * Throws JsonPatchError on failure.
 */
export function applyJsonPatch<T>(doc: T, operations: JsonPatchOperation[]): T {
  let result: unknown = doc
  for (let i = 0; i < operations.length; i++) {
    const op = operations[i]
    try {
      result = applySingleOp(result, op)
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      throw new JsonPatchError(message, op, i)
    }
  }
  return result as T
}

// ---- internal helpers ----

function applySingleOp(doc: unknown, op: JsonPatchOperation): unknown {
  switch (op.op) {
    case "add":
      return addByPath(doc, op.path, op.value)
    case "remove":
      return removeByPath(doc, op.path)
    case "replace": {
      // Verify the path exists first
      getByPath(doc, op.path)
      return setByPath(doc, op.path, op.value)
    }
    case "move": {
      const value = getByPath(doc, op.from)
      const removed = removeByPath(doc, op.from)
      return addByPath(removed, op.path, value)
    }
    case "copy": {
      const value = getByPath(doc, op.from)
      return addByPath(doc, op.path, value)
    }
    case "test": {
      const actual = getByPath(doc, op.path)
      if (!deepEqual(actual, op.value)) {
        throw new Error(
          `Test failed: value at "${op.path}" is ${JSON.stringify(actual)}, expected ${JSON.stringify(op.value)}`
        )
      }
      return doc
    }
    default:
      throw new Error(`Unknown operation: ${(op as { op: string }).op}`)
  }
}

function parseArrayIndex(seg: string, length: number, allowAppend: boolean): number {
  if (seg === "-") {
    if (!allowAppend) throw new Error('Cannot use "-" for get/remove on arrays')
    return length
  }
  if (!/^(0|[1-9]\d*)$/.test(seg)) {
    throw new Error(`Invalid array index: "${seg}"`)
  }
  const index = Number(seg)
  const max = allowAppend ? length : length - 1
  if (index > max) {
    throw new Error(`Array index ${index} out of bounds (length ${length})`)
  }
  return index
}

function immutableSet(
  obj: unknown,
  ctx: { segments: string[]; depth: number; value: unknown }
): unknown {
  const seg = ctx.segments[ctx.depth]
  const isLast = ctx.depth === ctx.segments.length - 1

  if (Array.isArray(obj)) {
    const index = parseArrayIndex(seg, obj.length, false)
    const copy = [...obj]
    copy[index] = isLast ? ctx.value : immutableSet(obj[index], { ...ctx, depth: ctx.depth + 1 })
    return copy
  }

  if (obj !== null && typeof obj === "object") {
    const record = obj as Record<string, unknown>
    return {
      ...record,
      [seg]: isLast ? ctx.value : immutableSet(record[seg], { ...ctx, depth: ctx.depth + 1 }),
    }
  }

  throw new Error(`Cannot set on non-object/non-array at segment "${seg}"`)
}

function immutableAdd(
  obj: unknown,
  ctx: { segments: string[]; depth: number; value: unknown }
): unknown {
  const seg = ctx.segments[ctx.depth]
  const isLast = ctx.depth === ctx.segments.length - 1

  if (Array.isArray(obj)) {
    if (isLast) {
      const index = parseArrayIndex(seg, obj.length, true)
      const copy = [...obj]
      copy.splice(index, 0, ctx.value)
      return copy
    }
    const index = parseArrayIndex(seg, obj.length, false)
    const copy = [...obj]
    copy[index] = immutableAdd(obj[index], { ...ctx, depth: ctx.depth + 1 })
    return copy
  }

  if (obj !== null && typeof obj === "object") {
    const record = obj as Record<string, unknown>
    if (isLast) {
      return { ...record, [seg]: ctx.value }
    }
    return {
      ...record,
      [seg]: immutableAdd(record[seg], { ...ctx, depth: ctx.depth + 1 }),
    }
  }

  throw new Error(`Cannot add on non-object/non-array at segment "${seg}"`)
}

function immutableRemove(obj: unknown, segments: string[], depth: number): unknown {
  const seg = segments[depth]
  const isLast = depth === segments.length - 1

  if (Array.isArray(obj)) {
    const index = parseArrayIndex(seg, obj.length, false)
    if (isLast) {
      const copy = [...obj]
      copy.splice(index, 1)
      return copy
    }
    const copy = [...obj]
    copy[index] = immutableRemove(obj[index], segments, depth + 1)
    return copy
  }

  if (obj !== null && typeof obj === "object") {
    const record = obj as Record<string, unknown>
    if (isLast) {
      if (!(seg in record)) {
        throw new Error(`Property "${seg}" does not exist`)
      }
      const { [seg]: _, ...rest } = record
      return rest
    }
    if (!(seg in record)) {
      throw new Error(`Property "${seg}" does not exist`)
    }
    return {
      ...record,
      [seg]: immutableRemove(record[seg], segments, depth + 1),
    }
  }

  throw new Error(`Cannot remove from non-object/non-array at segment "${seg}"`)
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  if (typeof a !== typeof b) return false

  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false
    if (a.length !== b.length) return false
    return a.every((item, i) => deepEqual(item, b[i]))
  }

  if (typeof a === "object") {
    const aObj = a as Record<string, unknown>
    const bObj = b as Record<string, unknown>
    const aKeys = Object.keys(aObj)
    const bKeys = Object.keys(bObj)
    if (aKeys.length !== bKeys.length) return false
    return aKeys.every((key) => key in bObj && deepEqual(aObj[key], bObj[key]))
  }

  return false
}
